import { useEffect, useMemo, useState } from "react";
import DashboardShell from "../components/shell/DashboardShell";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash2, Pencil, ChevronLeft, ChevronRight, CalendarDays, List, AlertTriangle, CheckCircle2, Clock, XCircle, FileBarChart, Download, Mail, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { WorkOrderPhotoBadge } from "../components/WorkOrderPhotos";
import USDateInput from "../components/USDateInput";
import { formatUSD, parseUSD } from "../lib/currency";

const EMPTY = {
  title: "",
  description: "",
  property_id: "",
  vendor_id: "",
  scheduled_date: new Date().toISOString().slice(0, 10),
  status: "open",
  priority: "medium",
  estimated_cost: 0,
  actual_cost: 0,
  notes: "",
};

const STATUS_STYLES = {
  open:         { bg: "bg-blue-100",    fg: "text-blue-800",    ring: "ring-blue-300",    Icon: Clock,         label: "Open" },
  in_progress:  { bg: "bg-amber-100",   fg: "text-amber-800",   ring: "ring-amber-300",   Icon: AlertTriangle, label: "In progress" },
  completed:    { bg: "bg-emerald-100", fg: "text-emerald-800", ring: "ring-emerald-300", Icon: CheckCircle2,  label: "Completed" },
  cancelled:    { bg: "bg-slate-100",   fg: "text-slate-600",   ring: "ring-slate-300",   Icon: XCircle,       label: "Cancelled" },
};
const PRIORITY_DOT = { emergency: "bg-rose-700 ring-2 ring-rose-300", high: "bg-red-500", medium: "bg-amber-500", low: "bg-slate-400" };

export default function Maintenance() {
  const [items, setItems] = useState([]);
  const [byStatus, setByStatus] = useState({ open: 0, in_progress: 0, completed: 0, cancelled: 0 });
  const [vendors, setVendors] = useState([]);
  const [properties, setProperties] = useState([]);

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingNumber, setEditingNumber] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const [view, setView] = useState("calendar"); // "calendar" | "list" | "report"
  const [statusFilter, setStatusFilter] = useState("");
  const [onlyUndispatched, setOnlyUndispatched] = useState(false);
  // Month anchor for calendar — first day of the visible month
  const [monthAnchor, setMonthAnchor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const load = async () => {
    try {
      const [wo, v, p] = await Promise.all([
        api.get("/maintenance"),
        api.get("/vendors"),
        api.get("/properties"),
      ]);
      setItems(wo.data.items || []);
      setByStatus(wo.data.by_status || { open: 0, in_progress: 0, completed: 0, cancelled: 0 });
      setVendors(v.data.items || []);
      setProperties(p.data.items || p.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Load failed");
    }
  };
  useEffect(() => { load(); }, []);

  const openAdd = (prefilledDate) => {
    setEditingId(null);
    setEditingNumber(null);
    setForm({ ...EMPTY, scheduled_date: prefilledDate || new Date().toISOString().slice(0, 10) });
    setOpen(true);
  };
  const openEdit = (wo) => {
    setEditingId(wo.id);
    setEditingNumber(wo.wo_number_display || null);
    setForm({
      title: wo.title || "",
      description: wo.description || "",
      property_id: wo.property_id || "",
      vendor_id: wo.vendor_id || "",
      scheduled_date: wo.scheduled_date,
      status: wo.status || "open",
      priority: wo.priority || "medium",
      estimated_cost: wo.estimated_cost || 0,
      actual_cost: wo.actual_cost || 0,
      notes: wo.notes || "",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      estimated_cost: Number(form.estimated_cost) || 0,
      actual_cost: Number(form.actual_cost) || 0,
      vendor_id: form.vendor_id || null,
      property_id: form.property_id || null,
    };
    try {
      if (editingId) {
        await api.put(`/maintenance/${editingId}`, payload);
        toast.success("Work order updated");
      } else {
        await api.post("/maintenance", payload);
        toast.success("Work order created");
      }
      setOpen(false);
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Delete work order "${title}"?`)) return;
    try {
      await api.delete(`/maintenance/${id}`);
      toast.success("Work order deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const sendToVendor = async (wo) => {
    if (!wo?.id) return;
    if (!wo.vendor_id) { toast.error("Assign a vendor first."); return; }
    try {
      const { data } = await api.post(`/maintenance/${wo.id}/send-to-vendor`);
      const delivered = data?.delivery?.status;
      if (delivered === "sent") {
        toast.success(`Sent to ${data.vendor_email}`);
      } else if (delivered === "mocked") {
        toast.success(`Queued for ${data.vendor_email} (MOCK — add RESEND_API_KEY to go live)`);
      } else {
        toast.error("Email failed — check server logs.");
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
    }
  };

  /** Generate a printable one-page PDF and email it to the vendor as an
   *  attachment (in addition to the inline HTML body + photos). Vendors
   *  typically print and file it alongside their other paperwork. */
  const sendToVendorWithPdf = async (wo) => {
    if (!wo?.id) return;
    if (!wo.vendor_id) { toast.error("Assign a vendor first."); return; }
    try {
      const { data } = await api.post(
        `/maintenance/${wo.id}/send-to-vendor`,
        null,
        { params: { attach_pdf: true } },
      );
      const delivered = data?.delivery?.status;
      if (delivered === "sent") {
        toast.success(`PDF emailed to ${data.vendor_email}`);
      } else if (delivered === "mocked") {
        toast.success(`PDF queued for ${data.vendor_email} (MOCK — add RESEND_API_KEY to go live)`);
      } else {
        toast.error("Email failed — check server logs.");
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
    }
  };

  /** Dispatch every currently-undispatched WO in the filtered view in
   *  parallel. Fires POST /maintenance/{id}/send-to-vendor for each row that
   *  still has no `last_sent_to_vendor_at` timestamp. Shows an aggregate
   *  "Dispatched X of Y" toast when done. Meant as the one-click morning
   *  routine pair for the "Not yet sent" filter chip. */
  const dispatchAll = async () => {
    const queue = filtered.filter((w) => w.vendor_id && !w.last_sent_to_vendor_at);
    if (queue.length === 0) {
      toast.message("Nothing to dispatch — all WOs are already sent");
      return;
    }
    if (!window.confirm(`Email ${queue.length} work order${queue.length === 1 ? "" : "s"} to their assigned vendors now?`)) return;

    const results = await Promise.allSettled(
      queue.map((wo) => api.post(`/maintenance/${wo.id}/send-to-vendor`)),
    );
    const ok = results.filter((r) => r.status === "fulfilled" && ["sent", "mocked"].includes(r.value?.data?.delivery?.status)).length;
    const failed = queue.length - ok;
    if (failed === 0) {
      toast.success(`Dispatched ${ok} of ${queue.length} work orders`);
    } else {
      toast.warning(`Dispatched ${ok} of ${queue.length} — ${failed} failed`);
    }
    load();
  };

  /** Download the printable one-page PDF and open it in a new tab.
   *  Uses the authenticated api client (JWT Bearer) → fetches as blob →
   *  object URL → window.open(_blank). Revokes the URL after 10s to free
   *  memory. Works for any work order regardless of vendor assignment. */
  const downloadWorkOrderPdf = async (wo) => {
    if (!wo?.id) return;
    try {
      const res = await api.get(`/maintenance/${wo.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank");
      if (!win) {
        // Popup blocked — fall back to a hidden download link
        const a = document.createElement("a");
        a.href = url;
        a.download = `work-order-${wo.wo_number_display || wo.id.slice(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "PDF download failed");
    }
  };

  // Count vendor-assigned WOs that have never been emailed — the landlord's
  // morning dispatch queue. Cheap derived value, recomputed on items change.
  const undispatchedCount = useMemo(
    () => items.filter((i) => i.vendor_id && !i.last_sent_to_vendor_at).length,
    [items],
  );

  const filtered = useMemo(() => {
    let out = items;
    if (statusFilter) out = out.filter((i) => i.status === statusFilter);
    if (onlyUndispatched) out = out.filter((i) => i.vendor_id && !i.last_sent_to_vendor_at);
    return out;
  }, [items, statusFilter, onlyUndispatched]);

  return (
    <DashboardShell>
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
          <div>
            <div className="overline text-slate-500">Operations</div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit" }} data-testid="maintenance-heading">
              Maintenance Requests
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {byStatus.open} open · {byStatus.in_progress} in progress · {byStatus.completed} completed
            </p>
          </div>
          <button data-testid="add-work-order-button" onClick={() => openAdd()} className="btn-primary-dark flex items-center gap-2 text-sm">
            <Plus className="w-4 h-4" /> New Work Order
          </button>
        </div>

        {/* View toggle + status filter */}
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div className="inline-flex rounded-sm border border-slate-200 overflow-hidden bg-white">
            <button
              onClick={() => setView("calendar")}
              data-testid="maintenance-view-calendar"
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest flex items-center gap-1.5 ${view === "calendar" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
            >
              <CalendarDays className="w-3.5 h-3.5" /> Calendar
            </button>
            <button
              onClick={() => setView("list")}
              data-testid="maintenance-view-list"
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest flex items-center gap-1.5 ${view === "list" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
            >
              <List className="w-3.5 h-3.5" /> List
            </button>
            <button
              onClick={() => setView("report")}
              data-testid="maintenance-view-report"
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest flex items-center gap-1.5 ${view === "report" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
            >
              <FileBarChart className="w-3.5 h-3.5" /> Report
            </button>
          </div>
          <div className={`flex items-center gap-3 ${view === "report" ? "invisible" : ""}`}>
            {undispatchedCount > 0 && (
              <button
                type="button"
                onClick={() => setOnlyUndispatched(v => !v)}
                data-testid="maintenance-undispatched-filter"
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-mono uppercase tracking-widest border transition-colors ${
                  onlyUndispatched
                    ? "bg-amber-500 text-white border-amber-500"
                    : "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100"
                }`}
                title="Show only work orders that have a vendor assigned but haven't been emailed yet — your morning dispatch queue"
              >
                <Mail className="w-3.5 h-3.5" /> Not yet sent ({undispatchedCount})
                {onlyUndispatched && <XCircle className="w-3.5 h-3.5 opacity-80" />}
              </button>
            )}
            {onlyUndispatched && undispatchedCount > 0 && (
              <button
                type="button"
                onClick={dispatchAll}
                data-testid="maintenance-dispatch-all-button"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-mono uppercase tracking-widest bg-blue-600 text-white border border-blue-600 hover:bg-blue-700 transition-colors"
                title="Email every visible work order to its assigned vendor in one click"
              >
                <Mail className="w-3.5 h-3.5" /> Dispatch all
              </button>
            )}
            <select
              className="input-clean text-sm"
              data-testid="maintenance-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All statuses ({items.length})</option>
              <option value="open">Open ({byStatus.open})</option>
              <option value="in_progress">In progress ({byStatus.in_progress})</option>
              <option value="completed">Completed ({byStatus.completed})</option>
              <option value="cancelled">Cancelled ({byStatus.cancelled})</option>
            </select>
          </div>
        </div>

        {view === "calendar" ? (
          <CalendarView
            items={filtered}
            monthAnchor={monthAnchor}
            onPrev={() => setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() - 1, 1))}
            onNext={() => setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 1))}
            onToday={() => { const d = new Date(); setMonthAnchor(new Date(d.getFullYear(), d.getMonth(), 1)); }}
            onDayClick={(iso) => openAdd(iso)}
            onWorkOrderClick={openEdit}
          />
        ) : view === "list" ? (
          <ListView items={filtered} onEdit={openEdit} onDelete={remove} onSendToVendor={sendToVendor} onDownloadPdf={downloadWorkOrderPdf} />
        ) : (
          <ReportView onEdit={openEdit} onDelete={async (id, title) => { await remove(id, title); }} onSendToVendor={sendToVendor} onDownloadPdf={downloadWorkOrderPdf} />
        )}

        {/* Create / Edit dialog */}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
                {editingId && editingNumber && (
                  <span className="inline-block bg-slate-900 text-white text-[11px] font-mono px-2 py-0.5 rounded-sm" data-testid="wo-dialog-number">{editingNumber}</span>
                )}
                {editingId ? "Edit work order" : "New work order"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={save} className="space-y-3">
              <Field label="Title">
                <input
                  required
                  className="input-clean w-full"
                  data-testid="wo-title-input"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g., Fix leaking sink in Unit 2B"
                />
              </Field>
              <Field label="Description">
                <textarea
                  rows={2}
                  className="input-clean w-full"
                  data-testid="wo-description-input"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Details about the issue, access notes, etc."
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Property">
                  <select
                    className="input-clean w-full"
                    data-testid="wo-property-select"
                    value={form.property_id}
                    onChange={(e) => setForm({ ...form, property_id: e.target.value })}
                  >
                    <option value="">— None —</option>
                    {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </Field>
                <Field label="Vendor">
                  <select
                    className="input-clean w-full"
                    data-testid="wo-vendor-select"
                    value={form.vendor_id}
                    onChange={(e) => setForm({ ...form, vendor_id: e.target.value })}
                  >
                    <option value="">— None —</option>
                    {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}{v.category ? ` · ${v.category}` : ""}</option>)}
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Scheduled date">
                  <USDateInput
                    required
                    data-testid="wo-date-input"
                    value={form.scheduled_date}
                    onChange={(iso) => setForm({ ...form, scheduled_date: iso })}
                  />
                </Field>
                <Field label="Priority">
                  <select
                    className="input-clean w-full"
                    data-testid="wo-priority-select"
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="emergency">🚨 Emergency</option>
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Status">
                  <select
                    className="input-clean w-full"
                    data-testid="wo-status-select"
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </Field>
                <Field label="Est. cost">
                  <input
                    type="text"
                    inputMode="decimal"
                    className="input-clean w-full font-mono"
                    data-testid="wo-est-cost-input"
                    value={formatUSD(form.estimated_cost)}
                    onChange={(e) => setForm({ ...form, estimated_cost: parseUSD(e.target.value) })}
                    placeholder="$0.00"
                  />
                </Field>
                <Field label="Actual cost">
                  <input
                    type="text"
                    inputMode="decimal"
                    className="input-clean w-full font-mono"
                    data-testid="wo-actual-cost-input"
                    value={formatUSD(form.actual_cost)}
                    onChange={(e) => setForm({ ...form, actual_cost: parseUSD(e.target.value) })}
                    placeholder="$0.00"
                  />
                </Field>
              </div>
              <Field label="Notes">
                <textarea
                  rows={2}
                  className="input-clean w-full"
                  data-testid="wo-notes-input"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="optional"
                />
              </Field>
              <DialogFooter>
                {editingId && (
                  <button
                    type="button"
                    onClick={() => { setOpen(false); remove(editingId, form.title); }}
                    className="text-red-600 hover:text-red-800 text-sm mr-auto flex items-center gap-1"
                    data-testid="wo-delete-button"
                  >
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                )}
                {editingId && form.vendor_id && (
                  <button
                    type="button"
                    onClick={async () => { await sendToVendor({ id: editingId, vendor_id: form.vendor_id }); }}
                    className="btn-outline-dark text-sm flex items-center gap-2"
                    data-testid="wo-send-to-vendor-button"
                    title="Email this work order to the assigned vendor"
                  >
                    <Mail className="w-4 h-4" /> Send to vendor
                  </button>
                )}
                {editingId && form.vendor_id && (
                  <button
                    type="button"
                    onClick={async () => { await sendToVendorWithPdf({ id: editingId, vendor_id: form.vendor_id }); }}
                    className="btn-outline-dark text-sm flex items-center gap-2"
                    data-testid="wo-send-pdf-to-vendor-button"
                    title="Generate a printable PDF and email it to the vendor"
                  >
                    <FileText className="w-4 h-4" /> Send PDF to vendor
                  </button>
                )}
                <button type="button" onClick={() => setOpen(false)} className="btn-outline-dark">Cancel</button>
                <button type="submit" className="btn-primary-dark" data-testid="wo-submit-button">
                  {editingId ? "Save changes" : "Create work order"}
                </button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardShell>
  );
}

function CalendarView({ items, monthAnchor, onPrev, onNext, onToday, onDayClick, onWorkOrderClick }) {
  const year = monthAnchor.getFullYear();
  const month = monthAnchor.getMonth();
  const firstDow = new Date(year, month, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date(); today.setHours(0, 0, 0, 0);

  // Group work orders by ISO date string
  const byDate = useMemo(() => {
    const map = {};
    for (const wo of items) {
      const key = (wo.scheduled_date || "").slice(0, 10);
      if (!map[key]) map[key] = [];
      map[key].push(wo);
    }
    return map;
  }, [items]);

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const monthName = monthAnchor.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="maintenance-calendar">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
        <div className="font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>{monthName}</div>
        <div className="flex items-center gap-1">
          <button onClick={onPrev} className="p-1.5 hover:bg-white rounded-sm text-slate-600" title="Previous" data-testid="calendar-prev">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={onToday} className="px-2 py-1 text-[11px] font-mono uppercase tracking-widest hover:bg-white rounded-sm text-slate-600" data-testid="calendar-today">
            Today
          </button>
          <button onClick={onNext} className="p-1.5 hover:bg-white rounded-sm text-slate-600" title="Next" data-testid="calendar-next">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50/60">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="px-2 py-2 text-[10px] uppercase tracking-widest font-mono text-slate-500 text-center">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 auto-rows-[120px]">
        {cells.map((day, i) => {
          // Calendar cells: compose key from year+month+position so React
          // remounts cells correctly when the user navigates to a different month.
          const cellKey = day === null ? `empty-${year}-${month}-${i}` : `cell-${year}-${month}-${day}`;
          if (day === null) return <div key={cellKey} className="border-b border-r border-slate-100 bg-slate-50/30" />;
          const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const events = byDate[iso] || [];
          const dDate = new Date(year, month, day);
          const isToday = dDate.getTime() === today.getTime();
          return (
            <div
              key={cellKey}
              onClick={() => onDayClick(iso)}
              data-testid={`calendar-day-${iso}`}
              className={`border-b border-r border-slate-100 p-1.5 text-xs cursor-pointer hover:bg-slate-50 relative ${isToday ? "bg-blue-50/50" : ""}`}
            >
              <div className={`flex items-center justify-between mb-1 ${isToday ? "font-bold text-blue-700" : "text-slate-500"}`}>
                <span className="font-mono text-[11px]">{day}</span>
                {events.length > 0 && (
                  <span className="text-[9px] font-mono bg-slate-900 text-white px-1 rounded-sm">{events.length}</span>
                )}
              </div>
              <div className="space-y-0.5 overflow-hidden">
                {events.slice(0, 3).map((wo) => {
                  const s = STATUS_STYLES[wo.status] || STATUS_STYLES.open;
                  return (
                    <button
                      key={wo.id}
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onWorkOrderClick(wo); }}
                      data-testid={`calendar-event-${wo.id}`}
                      className={`w-full text-left px-1.5 py-0.5 rounded-sm text-[10px] font-mono truncate flex items-center gap-1 ${s.bg} ${s.fg} hover:ring-1 ${s.ring}`}
                      title={`${wo.wo_number_display ? wo.wo_number_display + " · " : ""}${wo.title}${wo.vendor_name ? ` — ${wo.vendor_name}` : ""}`}
                    >
                      <span className={`w-1 h-1 rounded-full shrink-0 ${PRIORITY_DOT[wo.priority] || PRIORITY_DOT.medium}`} />
                      <span className="truncate">{wo.title}</span>
                    </button>
                  );
                })}
                {events.length > 3 && (
                  <div className="text-[10px] font-mono text-slate-500 px-1.5">+{events.length - 3} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ListView({ items, onEdit, onDelete, onSendToVendor, onDownloadPdf }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm">
      <table className="w-full text-sm" data-testid="maintenance-list-table">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr className="text-left">
            <Th>Scheduled</Th>
            <Th>Work order</Th>
            <Th>Property</Th>
            <Th>Vendor</Th>
            <Th>Status</Th>
            <Th className="text-right">Est.</Th>
            <Th className="text-right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={7} className="text-center py-16 text-slate-400">No work orders.</td></tr>}
          {items.map((wo) => {
            const s = STATUS_STYLES[wo.status] || STATUS_STYLES.open;
            const S = s.Icon;
            return (
              <tr key={wo.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`wo-row-${wo.id}`}>
                <Td className="font-mono text-xs text-slate-600">{wo.scheduled_date}</Td>
                <Td>
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${PRIORITY_DOT[wo.priority] || PRIORITY_DOT.medium}`} title={`priority: ${wo.priority}`} />
                    {wo.wo_number_display && (
                      <span className="inline-block bg-slate-900 text-white text-[10px] font-mono px-1.5 py-0.5 rounded-sm" data-testid={`wo-number-${wo.id}`}>{wo.wo_number_display}</span>
                    )}
                    <span className="font-semibold text-slate-900">{wo.title}</span>
                    {wo.source === "tenant" && (
                      <span
                        className="inline-block bg-amber-50 border border-amber-200 text-amber-800 text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm"
                        title={`Reported by ${wo.submitted_by_tenant_name || "tenant"}`}
                        data-testid={`wo-source-tenant-${wo.id}`}
                      >
                        Tenant
                      </span>
                    )}
                    <WorkOrderPhotoBadge workOrderId={wo.id} />
                  </div>
                  {wo.description && <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{wo.description}</div>}
                </Td>
                <Td className="text-sm text-slate-700">{wo.property_name || <span className="text-slate-400">—</span>}</Td>
                <Td className="text-sm text-slate-700">
                  {wo.vendor_name || <span className="text-slate-400">—</span>}
                  {wo.vendor_category && <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{wo.vendor_category}</div>}
                  {wo.vendor_id && (
                    <VendorSentChip ts={wo.last_sent_to_vendor_at} testid={`sent-chip-${wo.id}`} />
                  )}
                </Td>
                <Td>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-mono uppercase tracking-widest ${s.bg} ${s.fg}`}>
                    <S className="w-3 h-3" /> {s.label}
                  </span>
                </Td>
                <Td className="text-right font-mono">${Number(wo.estimated_cost || 0).toLocaleString()}</Td>
                <Td className="text-right">
                  <button
                    onClick={() => onDownloadPdf(wo)}
                    className="text-slate-500 hover:text-slate-900 p-1 mr-1"
                    title="Download printable work-order PDF"
                    data-testid={`download-wo-pdf-${wo.id}`}
                  >
                    <FileText className="w-4 h-4" />
                  </button>
                  {wo.vendor_id && (
                    <button
                      onClick={() => onSendToVendor(wo)}
                      className="text-slate-500 hover:text-indigo-600 p-1 mr-1"
                      title={wo.last_sent_to_vendor_at ? `Last sent ${new Date(wo.last_sent_to_vendor_at).toLocaleString()}` : "Send to vendor"}
                      data-testid={`send-wo-${wo.id}`}
                    >
                      <Mail className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => onEdit(wo)}
                    className="text-slate-500 hover:text-slate-900 p-1 mr-1"
                    title="Edit"
                    data-testid={`edit-wo-${wo.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDelete(wo.id, wo.title)}
                    className="text-slate-500 hover:text-red-600 p-1"
                    title="Delete"
                    data-testid={`delete-wo-${wo.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, className = "" }) { return <th className={`px-5 py-3 overline text-slate-600 ${className}`}>{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-5 py-4 ${className}`}>{children}</td>; }
function Field({ label, children }) { return <label className="block"><div className="overline text-slate-600 mb-1">{label}</div>{children}</label>; }

/** "Sent Xh ago" / "Sent 2d ago" chip shown under a vendor name on work-order
 *  rows. Returns an amber "Not yet sent" pill when the vendor has been assigned
 *  but never emailed — makes un-dispatched work immediately visible to the
 *  landlord. Uses emerald tone once a send has happened. */
function VendorSentChip({ ts, testid }) {
  if (!ts) {
    return (
      <div
        data-testid={testid}
        className="mt-0.5 inline-block text-[10px] font-mono uppercase tracking-widest text-amber-700"
        title="This work order has not been emailed to the vendor yet"
      >
        · Not yet sent
      </div>
    );
  }
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHrs / 24);
  let rel;
  if (diffMin < 1) rel = "just now";
  else if (diffMin < 60) rel = `${diffMin}m ago`;
  else if (diffHrs < 24) rel = `${diffHrs}h ago`;
  else if (diffDays < 7) rel = `${diffDays}d ago`;
  else rel = d.toLocaleDateString();
  return (
    <div
      data-testid={testid}
      className="mt-0.5 inline-block text-[10px] font-mono uppercase tracking-widest text-emerald-700"
      title={`Last emailed to vendor ${d.toLocaleString()}`}
    >
      · Sent {rel}
    </div>
  );
}

// ----- REPORT VIEW -----
const STATUS_ORDER = ["open", "in_progress", "completed", "cancelled"];

function ReportView({ onEdit, onDelete, onSendToVendor, onDownloadPdf }) {
  // Default: last 90 days → today
  const [start, setStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 90);
    return d.toISOString().slice(0, 10);
  });
  const [end, setEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");

  const load = async () => {
    if (!start || !end) { toast.error("Pick a start and end date."); return; }
    if (start > end) { toast.error("Start date must be before end date."); return; }
    setLoading(true);
    try {
      const { data: d } = await api.get("/reports/maintenance", { params: { start_date: start, end_date: end } });
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load report.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const { data: blob } = await api.get("/reports/maintenance/pdf", {
        params: { start_date: start, end_date: end },
        responseType: "blob",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `maintenance-report_${start}_to_${end}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download PDF.");
    } finally {
      setDownloading(false);
    }
  };

  const emailPdf = async (e) => {
    e?.preventDefault?.();
    setSending(true);
    try {
      const params = { start_date: start, end_date: end };
      if (emailTo.trim()) params.to = emailTo.trim();
      const { data: res } = await api.post("/reports/maintenance/email", null, { params });
      const status = res?.delivery?.status;
      if (status === "sent") {
        toast.success(`Emailed to ${res.recipient}`);
      } else if (status === "mocked") {
        toast.success(`Queued for ${res.recipient} (email service in MOCK mode — add RESEND_API_KEY to go live)`);
      } else {
        toast.error("Email failed — check server logs.");
      }
      setEmailOpen(false);
      setEmailTo("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Send failed.");
    } finally {
      setSending(false);
    }
  };

  const byStatus = data?.by_status || { open: 0, in_progress: 0, completed: 0, cancelled: 0 };
  const totals = data?.totals || { estimated: 0, actual: 0 };
  const variance = (totals.actual || 0) - (totals.estimated || 0);

  return (
    <div className="space-y-6" data-testid="maintenance-report-view">
      {/* Controls */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 flex flex-wrap items-end gap-3">
        <Field label="Start date">
          <USDateInput data-testid="report-start-date" value={start} onChange={(iso) => setStart(iso)} />
        </Field>
        <Field label="End date">
          <USDateInput data-testid="report-end-date" value={end} onChange={(iso) => setEnd(iso)} />
        </Field>
        <button
          type="button"
          onClick={load}
          data-testid="report-run-button"
          disabled={loading}
          className="btn-primary-dark text-sm flex items-center gap-2 h-[38px]"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileBarChart className="w-4 h-4" />}
          Run report
        </button>
        <div className="flex-1" />
        <button
          type="button"
          onClick={downloadPdf}
          disabled={downloading || !data}
          data-testid="report-download-pdf"
          className="btn-outline-dark text-sm flex items-center gap-2 h-[38px]"
        >
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Download PDF
        </button>
        <button
          type="button"
          onClick={() => setEmailOpen(true)}
          disabled={!data}
          data-testid="report-email-pdf"
          className="btn-outline-dark text-sm flex items-center gap-2 h-[38px]"
        >
          <Mail className="w-4 h-4" />
          Email PDF
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="report-kpis">
        <KpiCard label="Open" count={byStatus.open} tone="text-blue-700" bg="bg-blue-50 border-blue-200" />
        <KpiCard label="In progress" count={byStatus.in_progress} tone="text-amber-700" bg="bg-amber-50 border-amber-200" />
        <KpiCard label="Completed" count={byStatus.completed} tone="text-emerald-700" bg="bg-emerald-50 border-emerald-200" />
        <KpiCard label="Cancelled" count={byStatus.cancelled} tone="text-slate-700" bg="bg-slate-50 border-slate-200" />
        <KpiCard label="Total" count={data?.items?.length || 0} tone="text-slate-900" bg="bg-white border-slate-200" />
      </div>

      {/* Cost summary */}
      <div className="grid grid-cols-3 gap-3" data-testid="report-costs">
        <CostTile label="Estimated cost" value={totals.estimated} />
        <CostTile label="Actual cost" value={totals.actual} />
        <CostTile label="Variance" value={variance} tone={variance > 0 ? "text-red-600" : variance < 0 ? "text-emerald-600" : "text-slate-600"} />
      </div>

      {/* Grouped work orders */}
      {data && data.items.length === 0 && (
        <div className="bg-white border border-dashed border-slate-200 rounded-sm p-12 text-center">
          <FileBarChart className="w-10 h-10 mx-auto text-slate-300 mb-2" />
          <div className="overline text-slate-500">No work orders</div>
          <p className="text-sm text-slate-500 mt-1">No work orders scheduled between {start} and {end}.</p>
        </div>
      )}

      {data && STATUS_ORDER.map((status) => {
        const group = (data.items || []).filter((wo) => (wo.status || "open") === status);
        if (group.length === 0) return null;
        const s = STATUS_STYLES[status];
        const S = s.Icon;
        return (
          <div key={status} className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid={`report-group-${status}`}>
            <div className={`px-5 py-3 border-b border-slate-200 flex items-center gap-2 ${s.bg} ${s.fg}`}>
              <S className="w-4 h-4" />
              <span className="font-bold text-sm" style={{ fontFamily: "Outfit" }}>{s.label}</span>
              <span className="text-xs font-mono">· {group.length} work order{group.length === 1 ? "" : "s"}</span>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left">
                  <Th>Scheduled</Th>
                  <Th>Title</Th>
                  <Th>Property</Th>
                  <Th>Vendor</Th>
                  <Th className="text-right">Est.</Th>
                  <Th className="text-right">Actual</Th>
                  <Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {group.map((wo) => (
                  <tr key={wo.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`report-row-${wo.id}`}>
                    <Td className="font-mono text-xs text-slate-600 whitespace-nowrap">{wo.scheduled_date}</Td>
                    <Td className="font-semibold text-slate-900">
                      <div className="flex items-center gap-2">
                        <span className={`inline-block w-2 h-2 rounded-full ${PRIORITY_DOT[wo.priority || "medium"]}`} title={`Priority: ${wo.priority}`} />
                        {wo.wo_number_display && (
                          <span className="inline-block bg-slate-900 text-white text-[10px] font-mono px-1.5 py-0.5 rounded-sm">{wo.wo_number_display}</span>
                        )}
                        {wo.title}
                      </div>
                      {wo.description && <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{wo.description}</div>}
                    </Td>
                    <Td className="text-xs text-slate-600">{wo.property_name || "—"}</Td>
                    <Td className="text-xs text-slate-600">
                      {wo.vendor_name || "—"}
                      {wo.vendor_id && (
                        <VendorSentChip ts={wo.last_sent_to_vendor_at} testid={`report-sent-chip-${wo.id}`} />
                      )}
                    </Td>
                    <Td className="text-right font-mono text-xs">${Number(wo.estimated_cost || 0).toLocaleString()}</Td>
                    <Td className="text-right font-mono text-xs">${Number(wo.actual_cost || 0).toLocaleString()}</Td>
                    <Td className="text-right whitespace-nowrap">
                      <button
                        onClick={() => onDownloadPdf(wo)}
                        className="text-slate-500 hover:text-slate-900 p-1 mr-1"
                        title="Download printable work-order PDF"
                        data-testid={`report-download-wo-pdf-${wo.id}`}
                      >
                        <FileText className="w-4 h-4" />
                      </button>
                      {wo.vendor_id && (
                        <button
                          onClick={() => onSendToVendor(wo)}
                          className="text-slate-500 hover:text-indigo-600 p-1 mr-1"
                          title={wo.last_sent_to_vendor_at ? `Last sent ${new Date(wo.last_sent_to_vendor_at).toLocaleString()}` : "Send to vendor"}
                          data-testid={`report-send-wo-${wo.id}`}
                        >
                          <Mail className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => onEdit(wo)}
                        className="text-slate-500 hover:text-slate-900 p-1 mr-1"
                        title="Edit"
                        data-testid={`report-edit-wo-${wo.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={async () => { await onDelete(wo.id, wo.title); load(); }}
                        className="text-slate-500 hover:text-red-600 p-1"
                        title="Delete"
                        data-testid={`report-delete-wo-${wo.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}

      {/* Email dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent data-testid="report-email-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}>Email maintenance report</DialogTitle>
          </DialogHeader>
          <form onSubmit={emailPdf} className="space-y-3">
            <p className="text-sm text-slate-600">
              Send the PDF for <b>{start}</b> → <b>{end}</b>. Leave blank to send to your own account email.
            </p>
            <Field label="Recipient email">
              <input
                type="email"
                className="input-clean w-full"
                data-testid="report-email-to"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="you@company.com (optional)"
              />
            </Field>
            <DialogFooter>
              <button type="button" className="btn-outline-dark" onClick={() => setEmailOpen(false)}>Cancel</button>
              <button type="submit" className="btn-primary-dark flex items-center gap-2" disabled={sending} data-testid="report-email-send">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ label, count, tone, bg }) {
  return (
    <div className={`border rounded-sm p-4 ${bg}`}>
      <div className="overline text-slate-500">{label}</div>
      <div className={`text-3xl font-bold font-mono mt-1 ${tone}`}>{count}</div>
    </div>
  );
}

function CostTile({ label, value, tone = "text-slate-900" }) {
  const n = Number(value) || 0;
  const sign = n < 0 ? "−" : "";
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="overline text-slate-500">{label}</div>
      <div className={`text-xl font-bold font-mono mt-1 ${tone}`}>
        {sign}${Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}
      </div>
    </div>
  );
}
